import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateMyGarageToken } from '@/services/api/mygarage-auth';
import { getUserArtisanOrders } from '@/services/api/artisan-order-comments';
import { ArtisanOrder } from '@/services/api/artisan-orders';
import OrderStatusProgress from '@/components/OrderStatusProgress';

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

const UserOrdersView: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<ArtisanOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        let uid = '';
        try {
          const user = await validateMyGarageToken();
          if (user) uid = user.id;
        } catch (e) { console.error('MyGarage認証エラー:', e); }
        if (!uid) {
          const smsUserId = localStorage.getItem('aineko-sms-user-id');
          if (smsUserId) { uid = smsUserId; }
          else { setError('ログインが必要です'); setLoading(false); return; }
        }
        const data = await getUserArtisanOrders(uid);
        setOrders(data);
      } catch { setError('注文の取得に失敗しました'); }
      finally { setLoading(false); }
    };
    init();
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/aineko')}>
            <span className="text-2xl">🐱</span>
            <span className="font-bold text-lg">注文管理</span>
          </div>
          <button onClick={() => navigate('/aineko')} className="text-sm text-gray-400 hover:text-white transition-colors">
            ← トップに戻る
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center text-gray-400 py-16">読み込み中...</div>
        ) : error ? (
          <div className="text-center text-red-400 py-16">{error}</div>
        ) : orders.length === 0 ? (
          <div className="text-center text-gray-500 py-16">
            <div className="text-5xl mb-4">📦</div>
            <p>注文はまだありません</p>
          </div>
        ) : (
          <div className="space-y-5">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden"
              >
                {/* ヘッダー: 注文日 + 注文番号 + 金額 */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-900/60 border-b border-gray-700/50">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-white">{formatDate(order.created_at)}</span>
                    <span className="text-sm text-gray-400">注文 #{order.id}</span>
                    {order.with_installation && (
                      <span className="text-xs text-blue-400 bg-blue-900/30 px-1.5 py-0.5 rounded">施工込</span>
                    )}
                  </div>
                  {order.status === 'cancelled' ? (
                    <span className="text-sm font-bold text-red-400 bg-red-900/30 border border-red-700/50 px-2.5 py-1 rounded">キャンセル</span>
                  ) : (
                    <span className="text-lg font-bold text-orange-400">¥{order.total_amount.toLocaleString()}</span>
                  )}
                </div>

                {/* 画像 + 注文情報 */}
                <div className="flex">
                  {/* 画像 */}
                  {order.image_url && (
                    <div
                      className="w-32 sm:w-40 flex-shrink-0 cursor-pointer relative group"
                      onClick={() => setExpandedImage(order.image_url)}
                    >
                      <img src={order.image_url} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 text-white text-2xl transition-opacity">🔍</span>
                      </div>
                    </div>
                  )}

                  {/* 注文情報 */}
                  <div className="flex-1 p-4 min-w-0">
                    <h3 className="text-base font-bold text-white leading-tight mb-3">
                      {order.product_name}
                      {order.variation_name && <span className="text-gray-400 font-normal text-sm ml-1">/ {order.variation_name}</span>}
                    </h3>

                    {/* 納期・配送情報 */}
                    {(order.estimated_delivery_date || order.tracking_number || order.shipping_carrier) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                        {order.estimated_delivery_date && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-orange-500 text-xs">📅</span>
                            <span className="text-xs text-gray-400">納期</span>
                            <span className="text-xs text-orange-300 font-medium">{order.estimated_delivery_date}</span>
                          </div>
                        )}
                        {order.tracking_number && !order.with_installation && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-green-500 text-xs">🚚</span>
                            <span className="text-xs text-gray-400">{order.shipping_carrier || '配送'}</span>
                            <span className="text-xs text-green-300 font-medium">{order.tracking_number}</span>
                          </div>
                        )}
                        {order.with_installation && order.shipping_carrier && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-green-500 text-xs">🔧</span>
                            <span className="text-xs text-gray-400">取付店</span>
                            <span className="text-xs text-green-300 font-medium">{order.shipping_carrier}</span>
                            {order.tracking_number && (
                              <span className="text-xs text-gray-400 ml-1">予定: {order.tracking_number}</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* アルティザン住所 */}
                    {order.with_installation && order.shipping_carrier === 'アルティザン' && (
                      <div className="bg-gray-900/60 rounded-lg px-3 py-2 mb-3 text-xs text-gray-400 leading-relaxed">
                        📍 〒213-0014 神奈川県川崎市高津区新作3-25-13<br />
                        📞 050-5865-9672
                      </div>
                    )}

                    {/* アクションボタン */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/aineko/order/${order.id}`)}
                        className="bg-indigo-600/80 hover:bg-indigo-600 text-white text-xs font-medium px-4 py-1.5 rounded-full transition-colors"
                      >
                        💬 確認ボード
                      </button>
                      {order.status === 'shipped' && (
                        <button
                          onClick={() => navigate(`/aineko/order/${order.id}/review`)}
                          className="bg-orange-600/80 hover:bg-orange-600 text-white text-xs font-medium px-4 py-1.5 rounded-full transition-colors"
                        >
                          ⭐ {order.with_installation ? '取付・評価' : '到着・評価'}
                        </button>
                      )}
                      {order.review_rating && (
                        <span className="text-yellow-400 text-sm ml-auto">
                          {'★'.repeat(order.review_rating)}{'☆'.repeat(5 - order.review_rating)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* プログレスバー */}
                <div className="border-t border-gray-700/40 bg-gray-900/30 px-4">
                  <OrderStatusProgress status={order.status} compact withInstallation={order.with_installation} />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* 画像拡大モーダル */}
      {expandedImage && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="拡大画像"
            className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setExpandedImage(null)}
            className="absolute top-6 right-6 bg-gray-800/80 hover:bg-gray-700 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg transition-colors"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default UserOrdersView;
